import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, inject, ViewChild, ElementRef } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { tap, debounceTime, takeWhile, scan } from 'rxjs/operators';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { Bucket, File, Unit } from '@appbolaget/aware-model';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FileListComponent } from '../file-list/file-list.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatMenuModule } from '@angular/material/menu';
import { NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Droppable } from '@modules/dragdrop/directives/droppable.directive';
import { Draggable } from '@modules/dragdrop/directives/draggable.directive';
import { SecureImagePipe } from '@helpers/pipes';
import { config } from '@components/media/media.config';
import { MediaService } from '@components/media/media.service';
import { DeepFormControlPipe } from '@helpers/pipes/deep-form-control.pipe';
import { PaginatorDumbComponent } from '@components/paginator/paginator-dumb.component';
import { SnackbarService } from '@viewservices/snackbar.service';

export interface FilesystemResponse {
    buckets: Bucket[];
    files: File[];
    pagination: {
        next: boolean;
        page: string;
    };
}
@Component({
    selector: 'aw-file-library',
    templateUrl: 'file-library.component.html',
    styleUrls: ['./file-library.component.scss'],
    standalone: true,
    imports: [
        MatToolbarModule,
        MatButtonModule,
        Droppable,
        MatIconModule,
        NgFor,
        MatMenuModule,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        MatGridListModule,
        Draggable,
        FileListComponent,
        MatProgressSpinnerModule,
        TranslateModule,
        SecureImagePipe,
        DeepFormControlPipe,
        PaginatorDumbComponent,
    ],
})
export class FileLibraryComponent implements OnInit, OnDestroy {
    rootBucket: Bucket = new Bucket({ id: null });
    activeBucket: Bucket = this.rootBucket;
    activeView = 'grid';
    breadcrumbs: Array<Bucket> = [];
    items: Array<File | Bucket>;
    sorting: any;
    cols = 6;
    mediaConfig = config;
    searchForm = new FormControl<string>('');
    currentPage: number = 1;
    canGoNextPage: boolean = false;

    selectedItems: Set<File | Bucket> = new Set();

    HAS_FETCHED: boolean;
    IS_LOADING: boolean;
    SELECTED_ITEMS_HAS_BUCKET: boolean;
    IS_SEARCHING: boolean;
    ALIVE: boolean;

    @Input() config = {
        uploadParams: {},
    };
    @Input()
    options: any;
    @Input()
    unit: Unit;
    @Input()
    mediaDialogService: any;
    @Input()
    mode: 'client' | 'unit' = 'unit';

    @Input() client: string = null;

    @Output()
    itemsSelected = new EventEmitter();
    @Output()
    bucketEntered = new EventEmitter<Bucket>();

    @ViewChild('searchField') searchField: ElementRef;

    #snackbarService = inject(SnackbarService);

    constructor(
        public mediaService: MediaService,
        public bpObserver: BreakpointObserver,
    ) {
        this.sorting = this.mediaConfig.sorting[0];
    }

    applySorting(sortItem) {
        if (this.sorting.value === sortItem.value) {
            sortItem.order = this.sorting.order === 'asc' ? 'desc' : 'asc';
        }

        this.sorting = sortItem;

        this.get(this.activeBucket, 1, this.searchForm.value);
    }

    toggleSearch() {
        this.IS_SEARCHING = !this.IS_SEARCHING;

        if (this.IS_SEARCHING) {
            setTimeout(() => {
                this.searchField.nativeElement.focus();
            }, 300);
        }
    }

    goToPage(page: number) {
        this.get(this.activeBucket, page, this.searchForm.value);
    }

    ngOnInit() {
        this.ALIVE = true;

        const breakpoints = [Breakpoints.XLarge, Breakpoints.Large, Breakpoints.Medium, Breakpoints.Small];

        this.bpObserver
            .observe(breakpoints)
            .pipe(
                takeWhile(() => this.ALIVE),
                debounceTime(100),
                tap((val) => {
                    if (val.breakpoints[Breakpoints.XLarge]) {
                        this.cols = 8;
                    } else if (val.breakpoints[Breakpoints.Large]) {
                        this.cols = 6;
                    } else if (val.breakpoints[Breakpoints.Medium]) {
                        this.cols = 4;
                    } else if (val.breakpoints[Breakpoints.Small]) {
                        this.cols = 2;
                    }
                }),
            )
            .subscribe();

        this.searchForm.valueChanges
            .pipe(
                takeWhile(() => this.ALIVE),
                debounceTime(500),
                scan((_, curr) => {
                    if (curr.length > 2) {
                        this.get(this.activeBucket, 1, curr);
                    } else if (curr.length === 0) {
                        this.get(this.activeBucket, 1);
                    }
                    return curr;
                }, ''),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.ALIVE = false;
    }

    get(bucket: Bucket = this.activeBucket, page = this.currentPage, searchTerm?: string) {
        this.IS_LOADING = true;

        this.items = [];
        this.selectedItems.clear();
        this.bucketEntered.emit(bucket);
        this.activeBucket = bucket;

        this.mediaService.getBucketsAndFiles(bucket.uuid, this.sorting, this.mode, page, searchTerm, this.unit).subscribe({
            next: (result) => {
                this.items = [...result.buckets, ...result.files];
                this.currentPage = parseInt(result.pagination.page);
                this.canGoNextPage = result.pagination.next;

                this.IS_LOADING = false;

                this.HAS_FETCHED = true;
            },
            error: () => {
                this.IS_LOADING = false;
                this.HAS_FETCHED = true;
                this.#snackbarService.error('Ett fel uppstod, kunde inte hämta filer');
            },
        });
    }

    getDragScope(item: File | Bucket) {
        return item instanceof File ? ['file'] : ['bucket'];
    }

    getDropScope(item: File | Bucket) {
        if (item instanceof Bucket) {
            return ['file', 'bucket'];
        }

        return [];
    }

    getRoot() {
        this.breadcrumbs = [];

        this.searchForm.setValue('', { emitEvent: false });
        this.IS_SEARCHING = false;
        this.get(this.rootBucket);
    }

    isSelected(item): boolean {
        return this.selectedItems.has(item);
    }

    /**
     * When a bucket or file is double clicked
     * @param item Bucket | File
     */
    itemSelected(item: Bucket | File) {
        if (item instanceof Bucket) {
            this.viewBucket(item);
        } else if (item instanceof File) {
            if (this.options.expectsValue && this.mediaDialogService.canReturnSelectedFiles([item])) {
                this.mediaDialogService.returnFiles([item]);
            }
        }
    }

    onFileMoved(destination: File | Bucket, $event) {
        const item: File | Bucket = $event.dragData;

        if (destination.uuid === item.uuid) {
            return false;
        }

        if (item instanceof File) {
            if (item.bucket_id === destination.id) {
                return false;
            }

            item.bucket_id = destination.id;
        } else if (item instanceof Bucket) {
            if (item.parent_id === destination.id) {
                return false;
            }

            item.parent_id = destination.id;
        }

        this.items = this.items.filter((obj) => obj.uuid !== item.uuid);
        this.selectedItems.clear();

        this.mediaService.moveFile(item, destination.uuid).execute().subscribe();
    }

    refresh() {
        this.get(this.activeBucket);
    }

    setActiveView(view: string) {
        this.activeView = view;
    }

    toggleSelectItem(item: Bucket | File, $event) {
        if (item instanceof File) {
            if ((!$event.shiftKey && !$event.metaKey) || this.SELECTED_ITEMS_HAS_BUCKET) {
                if (!this.selectedItems.has(item)) {
                    this.selectedItems.clear();
                }

                this.SELECTED_ITEMS_HAS_BUCKET = false;
            }

            if (this.selectedItems.has(item)) {
                this.selectedItems.delete(item);
            } else {
                this.selectedItems.add(item);
            }
        } else if (item instanceof Bucket) {
            if (this.selectedItems.has(item)) {
                this.selectedItems.clear();

                this.SELECTED_ITEMS_HAS_BUCKET = false;
            } else {
                this.selectedItems.clear();
                this.selectedItems.add(item);

                this.SELECTED_ITEMS_HAS_BUCKET = true;
            }
        }

        this.itemsSelected.emit(this.selectedItems);
    }

    viewBucket(bucket: Bucket) {
        this.breadcrumbs.push(bucket);

        this.searchForm.setValue('', { emitEvent: false });
        this.IS_SEARCHING = false;
        this.get(bucket);
    }

    viewBucketFromBreadcrumbs(bucket: Bucket, index: number) {
        this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
        this.searchForm.setValue('', { emitEvent: false });
        this.IS_SEARCHING = false;

        this.get(bucket);
    }
}
